import { createFileRoute, Outlet } from "@tanstack/react-router";
import { redirectIfSignedIn } from "@/core/lib/guards";
import AuthShell from "@/ui/components/auth/shell";

/**
 * The unauthenticated shell — the port of the `(auth)` server layout.
 *
 * Both apps host their own copy of these routes, and each one keeps the account
 * on itself: the post-sign-in destination is resolved against the surfaces
 * *this* app serves, never the other origin.
 *
 * `beforeLoad` redirects anyone who already has a session to their home view,
 * so a signed-in visitor never sees the form — unless this app serves no
 * surface that session may use, in which case the form is exactly what they
 * should see.
 */
export const Route = createFileRoute("/_auth")({
  validateSearch: (search: Record<string, unknown>) => ({
    // Carried through the auth flow: where to land afterwards, and the address
    // to prefill. Both arrive from emails and from the guards' redirects.
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
    // Set by `requireView` when a *valid* session was turned away because this
    // app hosts no surface for it. The form is otherwise identical to the
    // signed-out one; this only explains why it is showing.
    denied: typeof search.denied === "string" ? search.denied : undefined,
  }),
  beforeLoad: ({ context }) => redirectIfSignedIn(context.queryClient),
  component: AuthLayout,
});

function AuthLayout() {
  return (
    <AuthShell>
      <Outlet />
    </AuthShell>
  );
}
